import cn from "classnames";
import { type ReactNode, useState } from "react";
import Anchor from "@/components/common/Anchor";
import Backdrop from "@/components/common/Backdrop";
import VideoService from "@/components/common/VideoService";
import { useGetVideoURL } from "@/components/common/VideoService/hooks";
import {
  AspectRatio,
  VideoDisplayVariant,
  type VideoSource,
} from "@/constants/media";
import { getAspectRatioClassName } from "@/utils/style/getAspectRatioClassName";
import s from "./VideoPlayer.module.scss";

export interface VideoPlayerProps {
  source: VideoSource;
  videoId?: string;
  renderThumb: () => ReactNode;
  aspectRatio?: AspectRatio;
  openIn?: VideoDisplayVariant;
}

export default function VideoPlayer(props: VideoPlayerProps) {
  const {
    source,
    videoId,
    renderThumb,
    aspectRatio = AspectRatio["16:9"],
    openIn = VideoDisplayVariant.Overlay,
  } = props;

  const [open, setOpen] = useState(false);
  const videoURI = useGetVideoURL(source, videoId);

  if (!videoId) return null;

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const aspectRatioClassName = getAspectRatioClassName(aspectRatio);
  const mediaContent = <VideoService source={source} videoId={videoId} />;

  return (
    <div className={cn(s.wrapper, aspectRatioClassName)}>
      {open && openIn === VideoDisplayVariant.PlayInPlace && (
        <>{mediaContent}</>
      )}
      {open && openIn === VideoDisplayVariant.Overlay && (
        <Backdrop open={open} onClose={handleClose}>
          <div className={cn(s.videoInOverlay, aspectRatioClassName)}>
            {mediaContent}
          </div>
        </Backdrop>
      )}
      {openIn === VideoDisplayVariant.NewWindow ? (
        <Anchor href={videoURI} openInNewTab className={s.opener}>
          {renderThumb()}
        </Anchor>
      ) : (
        !open && (
          <div onClick={handleOpen} className={s.opener}>
            {renderThumb()}
          </div>
        )
      )}
    </div>
  );
}
